import React from 'react';
import * as XLSX from 'xlsx';

// Read-only grid view of a worksheet (cells, merges, column widths), shared by the
// template preview and the uploaded-document preview.
const HEAD_MARKERS = ['Major Topic', 'No', 'SN', 'Student Name', 'Column', 'Time', 'Particulars'];

// Always read from A1 so merge coordinates (which are absolute) line up with the rows,
// even for sheets whose used range starts at column B or C.
export function parseSheet(name, ws) {
  if (!ws['!ref']) return { name, rows: [], width: 1, span: {}, skip: new Set(), headIdx: -1, cols: [] };
  const end = XLSX.utils.decode_range(ws['!ref']).e;
  const rows = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: '',
    blankrows: true,
    range: { s: { r: 0, c: 0 }, e: end },
  });
  const width = Math.max(end.c + 1, 1);
  const span = {};
  const skip = new Set();
  (ws['!merges'] || []).forEach((m) => {
    span[`${m.s.r}:${m.s.c}`] = { rows: m.e.r - m.s.r + 1, cols: m.e.c - m.s.c + 1 };
    for (let r = m.s.r; r <= m.e.r; r += 1) {
      for (let c = m.s.c; c <= m.e.c; c += 1) {
        if (r !== m.s.r || c !== m.s.c) skip.add(`${r}:${c}`);
      }
    }
  });
  const headIdx = rows.findIndex((r) => HEAD_MARKERS.includes(String(r[0]).trim()) || String(r[0]).startsWith('NIC Number'));
  return { name, rows, width, span, skip, headIdx, cols: ws['!cols'] || [] };
}

const colWidth = (cols, c) => {
  const col = cols[c];
  if (col?.wpx) return col.wpx;
  if (col?.wch) return col.wch * 7;
  return 72;
};

export function SheetTable({ sheet, headerRows, legacy }) {
  const { rows, width, span, skip, headIdx, cols } = sheet;
  const total = Array.from({ length: width }).reduce((sum, _, c) => sum + colWidth(cols, c), 0);
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-xs text-slate-900" style={{ width: Math.max(total, 600), tableLayout: 'fixed' }}>
        <colgroup>
          {Array.from({ length: width }).map((_, c) => (
            <col key={c} style={{ width: `${colWidth(cols, c)}px` }} />
          ))}
        </colgroup>
        <tbody>
          {rows.map((row, r) => {
            // Originals are shown as they are: a plain grid, no styling of our own.
            // Only the typed-from-paste formats get their heading highlight.
            const inTable = legacy && headIdx !== -1 && r >= headIdx;
            const isHead = legacy && headIdx !== -1 && r >= headIdx && r < headIdx + headerRows;
            const isGroup = inTable && !isHead && Boolean(span[`${r}:0`]?.cols > 1);
            return (
              <tr key={r}>
                {Array.from({ length: width }).map((_, c) => {
                  if (skip.has(`${r}:${c}`)) return null;
                  const s = span[`${r}:${c}`];
                  const value = row[c] ?? '';
                  const border = legacy ? (inTable ? 'border border-slate-800' : '') : 'border border-slate-300';
                  return (
                    <td
                      key={c}
                      rowSpan={s?.rows}
                      colSpan={s?.cols}
                      className={`whitespace-pre-wrap break-words align-top px-1.5 py-1 ${border} ${isHead ? 'bg-sky-100 text-center align-middle font-bold' : ''} ${isGroup ? 'bg-slate-200 font-bold' : ''} ${legacy && r < 2 ? 'text-sm font-bold' : ''}`}
                    >
                      {String(value)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
